import { useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Loader2, Mic, MicOff } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Voice-fill button for the Add Training dialog.
 * Long-press OR tap-to-toggle to record. Releases / second-tap stops recording.
 * Calls /api/voice-journal/transcribe → /api/trainings/parse-voice → invokes onParse(parsed).
 */
export default function TrainingVoiceButton({ onParse }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    if (recording || processing) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        try { stream.getTracks().forEach(t => t.stop()); } catch (e) { /* ignore */ }
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        chunksRef.current = [];
        if (blob.size < 200) {
          toast.error('Recording was too short');
          return;
        }
        await processBlob(blob);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const processBlob = async (blob) => {
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'training.webm');
      fd.append('language', 'en');
      const tx = await axios.post(`${API}/voice-journal/transcribe`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const transcript = (tx.data?.transcript || '').trim();
      if (!transcript) {
        toast.error("Couldn't hear anything — try again?");
        return;
      }
      const parsed = await axios.post(`${API}/trainings/parse-voice`, { transcript });
      if (parsed.data?.error) {
        toast.error(parsed.data.error);
        return;
      }
      onParse?.(parsed.data);
      toast.success('Form filled from voice');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Voice processing failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleClick = () => {
    if (processing) return;
    if (recording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <Button
      type="button"
      variant={recording ? 'destructive' : 'outline'}
      size="sm"
      onClick={handleClick}
      disabled={processing}
      data-testid="training-voice-btn"
    >
      {processing ? (
        <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Processing...</>
      ) : recording ? (
        <><MicOff className="w-4 h-4 mr-1" />Stop</>
      ) : (
        <><Mic className="w-4 h-4 mr-1" />Voice fill</>
      )}
    </Button>
  );
}
